import React from "react";
import alertIntelligenceAPI from "../services/AlertIntelligenceAPI";
import LoadingSpinner from "../common/LoadingSpinner";
import "../css/Common.css";
import "./css/AlertsIntelligenceHub.css";

/**
 * Dashboard view for the Alerts Intelligence Hub.
 * Shows summary stats, featured alerts, and category navigation.
 *
 * @param {Object} data - Dashboard data from API
 * @param {boolean} loading - Loading state
 * @param {string} error - Error message
 * @param {Array} categories - Available categories
 * @param {Function} onViewAlert - Handler for viewing an alert
 * @param {Function} onCategorySelect - Handler for selecting a category
 * @param {Function} onRefresh - Handler for refresh
 * @param {Function} onViewAllAlerts - Handler for viewing all alerts
 * @param {Function} onFilterCritical - Handler for filtering critical alerts
 * @param {Function} onFilterUnreviewed - Handler for filtering unreviewed alerts
 */
function AlertsDashboard({
  data,
  loading,
  error,
  categories,
  isMobile = false,
  onViewAlert,
  onCategorySelect,
  onRefresh,
  onViewAllAlerts,
  onFilterCritical,
  onFilterUnreviewed,
  onNavigateToScamMap,
}) {
  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="aih-dashboard">
        <LoadingSpinner message="Loading alerts..." />
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ═══════════════════════════════════════════════════════════════════════════

  if (error) {
    return (
      <div className="aih-dashboard">
        <div className="aih-error">
          <p>{error}</p>
          <button className="aih-retry-btn" onClick={onRefresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Extract data - map backend field names to frontend expectations
  const stats = {
    criticalCount: data?.criticalAlertCount || 0,
    activeCount: data?.totalActiveAlerts || 0,
  };
  const featuredAlerts = data?.featuredAlerts || [];
  const recentAlerts = data?.recentAlerts || [];
  const userStats = {
    unreviewedCount:
      (data?.totalActiveAlerts || 0) - (data?.reviewedByUserCount || 0),
  };
  // Category breakdown from dashboard data (category name -> count)
  const categoryBreakdown = data?.categoryBreakdown || {};

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Render a stat card.
   * On mobile the card uses a compact centred column layout (icon above value+label).
   */
  const renderStatCard = (
    label,
    value,
    icon,
    color = "default",
    onClick = null,
  ) => (
    <div
      className={`aih-stat-card aih-stat-${color}${onClick ? " aih-stat-clickable" : ""}${isMobile ? " aih-stat-card-mobile" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      title={onClick ? `Click to view ${label.toLowerCase()}` : undefined}
    >
      <span className="aih-stat-icon">{icon}</span>
      <div className="aih-stat-content">
        <span className="aih-stat-value">{value}</span>
        <span className="aih-stat-label">
          {isMobile ? label.replace(" Alerts", "") : label}
        </span>
      </div>
    </div>
  );

  /**
   * Render a featured alert card
   */
  const renderFeaturedAlert = (alert) => {
    const threatLabel = alertIntelligenceAPI.getThreatLevelLabel(
      alert.threatLevel,
    );
    const threatColor = alertIntelligenceAPI.getThreatLevelColor(
      alert.threatLevel,
    );

    return (
      <div
        key={alert.id}
        className={`aih-featured-card aih-threat-${threatLabel.toLowerCase()}`}
        onClick={() => onViewAlert(alert.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onViewAlert(alert.id)}
      >
        {/* Threat badge */}
        <div
          className="aih-threat-badge"
          style={{ backgroundColor: threatColor }}
        >
          {threatLabel}
        </div>

        {/* Peak activity indicator */}
        {alert.peakActivity && (
          <div className="aih-peak-badge" title="Currently at peak activity">
            ↑ Peak Activity
          </div>
        )}

        {/* Alert title */}
        <h3 className="aih-featured-title">{alert.title}</h3>

        {/* Category & date */}
        <div className="aih-featured-meta">
          <span className="aih-category-tag">
            {formatCategory(alert.category)}
          </span>
          <span className="aih-time">
            {alertIntelligenceAPI.formatRelativeTime(alert.createdAt)}
          </span>
        </div>

        {/* Description preview */}
        <p className="aih-featured-description">
          {truncateText(alert.description || alert.summary, 120)}
        </p>

        {/* Stats */}
        <div className="aih-featured-stats">
          <span title="Views">{formatNumber(alert.viewCount || 0)} views</span>
          <span title="Shares">
            {formatNumber(alert.shareCount || 0)} shares
          </span>
          {alert.relatedTrainingUuid && (
            <span title="Has training">Training available</span>
          )}
        </div>

        {/* User reviewed indicator */}
        {alert.userReviewed && (
          <div className="aih-reviewed-badge" title="You've reviewed this">
            ✓ Reviewed
          </div>
        )}
      </div>
    );
  };

  /**
   * Render a category card
   */
  const renderCategoryCard = (category) => {
    // Get count from categoryBreakdown (from dashboard data) or from category object
    const count =
      categoryBreakdown[category.name] ??
      category.alertCount ??
      category.count ??
      0;
    const hasAlerts = count > 0;

    return (
      <div
        key={category.name}
        className={`aih-category-card ${!hasAlerts ? "disabled" : "has-alerts"}`}
        onClick={() => hasAlerts && onCategorySelect(category.name)}
        role="button"
        tabIndex={hasAlerts ? 0 : -1}
        onKeyDown={(e) =>
          e.key === "Enter" && hasAlerts && onCategorySelect(category.name)
        }
        aria-disabled={!hasAlerts}
      >
        <span className="aih-category-name">{category.displayName}</span>
        <span className="aih-category-count">{count}</span>
      </div>
    );
  };

  /**
   * Render a recent alert row
   */
  const renderRecentAlert = (alert) => {
    const threatColor = alertIntelligenceAPI.getThreatLevelColor(
      alert.threatLevel,
    );

    return (
      <div
        key={alert.id}
        className="aih-recent-row"
        onClick={() => onViewAlert(alert.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && onViewAlert(alert.id)}
      >
        {/* Threat indicator */}
        <div
          className="aih-threat-dot"
          style={{ backgroundColor: threatColor }}
          title={`Threat level: ${alert.threatLevel}/10`}
        />

        {/* Title */}
        <div className="aih-recent-content">
          <span className="aih-recent-title">{alert.title}</span>
          <span className="aih-recent-meta">
            {formatCategory(alert.category)} •{" "}
            {alertIntelligenceAPI.formatRelativeTime(alert.createdAt)}
          </span>
        </div>

        {/* Status indicators */}
        <div className="aih-recent-status">
          {alert.peakActivity && (
            <span className="aih-peak-indicator" title="Peak activity">
              ↑
            </span>
          )}
          {alert.userReviewed && (
            <span className="aih-reviewed-indicator" title="Reviewed">
              ✓
            </span>
          )}
          {!alert.userReviewed && (
            <span className="aih-new-badge" title="New">
              New
            </span>
          )}
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="aih-dashboard">
      {/* Quick stats */}
      <div
        className={`aih-stats-row${isMobile ? " aih-stats-row-mobile" : ""}`}
      >
        {renderStatCard(
          "Critical Alerts",
          stats.criticalCount || 0,
          "!",
          "critical",
          stats.criticalCount > 0 ? onFilterCritical : null,
        )}
        {renderStatCard(
          "Active Alerts",
          stats.activeCount || 0,
          "•",
          "warning",
          stats.activeCount > 0 ? onViewAllAlerts : null,
        )}
        {renderStatCard(
          "Unreviewed",
          userStats.unreviewedCount || 0,
          "○",
          "info",
          userStats.unreviewedCount > 0 ? onFilterUnreviewed : null,
        )}
      </div>

      {/* Featured alerts section */}
      {featuredAlerts.length > 0 && (
        <section className="aih-section">
          <h2 className="aih-section-title">Featured Alerts</h2>
          <div className="aih-featured-grid">
            {featuredAlerts.slice(0, 4).map(renderFeaturedAlert)}
          </div>
        </section>
      )}

      {/* Categories grid */}
      {categories.length > 0 && (
        <section className="aih-section">
          <h2 className="aih-section-title">Browse by Category</h2>
          <div className="aih-categories-grid">
            {categories.map(renderCategoryCard)}
          </div>
        </section>
      )}

      {/* Recent alerts */}
      {recentAlerts.length > 0 && (
        <section className="aih-section">
          <h2 className="aih-section-title">Recent Alerts</h2>
          <div className="aih-recent-list">
            {recentAlerts.slice(0, 10).map(renderRecentAlert)}
          </div>
          {recentAlerts.length > 10 && (
            <button className="aih-show-more-btn" onClick={onViewAllAlerts}>
              Show More
            </button>
          )}
        </section>
      )}

      {/* Your activity summary */}
      {(userStats.totalViews > 0 || userStats.totalReviewed > 0) && (
        <section className="aih-section aih-activity-section">
          <h2 className="aih-section-title">Your Activity</h2>
          <div className="aih-activity-stats">
            <div className="aih-activity-stat">
              <span className="aih-activity-value">
                {userStats.totalViews || 0}
              </span>
              <span className="aih-activity-label">Alerts Viewed</span>
            </div>
            <div className="aih-activity-stat">
              <span className="aih-activity-value">
                {userStats.totalReviewed || 0}
              </span>
              <span className="aih-activity-label">Marked Reviewed</span>
            </div>
            <div className="aih-activity-stat">
              <span className="aih-activity-value">
                {userStats.totalShared || 0}
              </span>
              <span className="aih-activity-label">Shared with Family</span>
            </div>
            <div className="aih-activity-stat">
              <span className="aih-activity-value">
                {userStats.trainingsStarted || 0}
              </span>
              <span className="aih-activity-label">Trainings Started</span>
            </div>
          </div>
        </section>
      )}

      {/* Scam Activity Near You entry card */}
      {onNavigateToScamMap && (
        <section className="aih-section">
          <h2 className="aih-section-title">Local Scam Activity</h2>
          <div
            className="aih-scammap-entry-card"
            role="button"
            tabIndex={0}
            onClick={onNavigateToScamMap}
            onKeyDown={(e) => e.key === "Enter" && onNavigateToScamMap()}
          >
            <span className="aih-scammap-entry-icon">📍</span>
            <div className="aih-scammap-entry-text">
              <strong>Scam Activity Near You</strong>
              <p>Search for reported scams within miles of any address. See what's happening in your neighborhood.</p>
            </div>
            <span className="aih-scammap-entry-arrow">→</span>
          </div>
        </section>
      )}

      {/* Empty state */}
      {featuredAlerts.length === 0 && recentAlerts.length === 0 && (
        <div className="aih-empty-state">
          <span className="aih-empty-icon">—</span>
          <h3>No Active Alerts</h3>
          <p>
            There are no alerts to display right now. Check back later for
            updates on new scam threats.
          </p>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format category name for display
 */
function formatCategory(category) {
  if (!category) return "";
  return category
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Get icon for category - using simple text indicators
 */
function getCategoryIcon(category) {
  // Return first letter of category as a simple indicator
  if (!category) return "•";
  const formatted = formatCategory(category);
  return formatted.charAt(0).toUpperCase();
}

/**
 * Truncate text to specified length
 */
function truncateText(text, maxLength) {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

/**
 * Format large numbers with K/M suffixes
 */
function formatNumber(num) {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
  if (num >= 1000) return (num / 1000).toFixed(1) + "K";
  return num.toString();
}

export default AlertsDashboard;
